import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CurrencyEur,
  ShieldCheck,
  FileText,
  ArrowRight,
  Info,
} from "@phosphor-icons/react";

export default function Aides() {
  return (
    <section
      data-testid="aides-section"
      className="py-16 lg:py-20 bg-gradient-to-br from-emerald-50/60 via-white to-blue-50/40 border-y border-slate-100"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center"
        >
          <div>
            <div className="inline-flex items-center gap-2 bg-white text-emerald-700 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] border border-emerald-100 mb-4">
              <CurrencyEur size={14} weight="fill" /> Aides de l'État
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Des aides financières pour vos travaux
            </h2>
            <p className="text-slate-600 mt-4 text-base md:text-lg leading-relaxed">
              Selon votre situation, vos travaux peuvent être en partie ou
              totalement financés grâce aux dispositifs de l'État (
              <span className="font-semibold text-slate-800">CEE</span>,{" "}
              <span className="font-semibold text-slate-800">
                MaPrimeRénov'
              </span>
              ).
            </p>
            <p className="text-slate-600 mt-3 text-base leading-relaxed">
              Nous vous accompagnons dans toutes les démarches administratives
              pour maximiser vos aides.
            </p>

            <div className="mt-5 flex items-start gap-2 p-3 rounded-xl bg-amber-50/70 border border-amber-100">
              <Info
                size={18}
                weight="fill"
                className="text-amber-600 flex-shrink-0 mt-0.5"
              />
              <p className="text-sm text-slate-700 leading-relaxed">
                Le montant des aides dépend de votre profil, de votre logement
                et des travaux réalisés.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Link
                to="/eligibilite"
                data-testid="aides-eligibility-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold btn-primary-eco"
              >
                Tester mon éligibilité gratuitement{" "}
                <ArrowRight size={16} weight="bold" />
              </Link>
              <span className="text-sm text-slate-700 font-medium inline-flex items-center gap-1.5">
                <ShieldCheck size={16} weight="fill" className="text-emerald-600" />
                Étude gratuite et sans engagement.
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <AideCard
              title="MaPrimeRénov'"
              desc="Dispositif national d'aide à la rénovation énergétique pour les propriétaires, modulé selon les revenus."
              icon={FileText}
              accent="blue"
              testId="aide-mpr"
            />
            <AideCard
              title="CEE"
              desc="Certificats d'économies d'énergie : prime versée par les fournisseurs d'énergie pour soutenir vos travaux."
              icon={CurrencyEur}
              accent="emerald"
              testId="aide-cee"
            />
            <div
              data-testid="aide-info-card"
              className="sm:col-span-2 p-5 rounded-2xl bg-white border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
            >
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={18} weight="fill" className="text-blue-700" />
                <h4 className="font-heading font-semibold text-slate-900">
                  Accompagnement complet
                </h4>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Nous constituons votre dossier, vérifions votre éligibilité aux
                dispositifs et gérons les démarches administratives de bout en
                bout.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function AideCard({ title, desc, icon: Icon, accent, testId }) {
  const accentClasses =
    accent === "blue"
      ? "from-blue-600 to-blue-500"
      : "from-emerald-500 to-emerald-400";
  return (
    <div
      data-testid={testId}
      className="p-5 rounded-2xl bg-white border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_30px_rgba(30,64,175,0.08)] hover:border-blue-200 transition-all"
    >
      <div
        className={`w-10 h-10 rounded-lg bg-gradient-to-br ${accentClasses} flex items-center justify-center mb-3`}
      >
        <Icon size={18} weight="fill" color="#FFFFFF" />
      </div>
      <h4 className="font-heading font-semibold text-slate-900 mb-1">
        {title}
      </h4>
      <p className="text-xs text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}
